"use client";

import { formatCurrency } from "@/app/(general-header)/botb-rewards/utils";
import SavingsInfoModal from "@/app/(custom-header)/botb-pass-members-home/components/SavingsInfoModal";
import { publishModalClasses, publishModalData, publishModalVisibility } from "@/app/redux/features/modal/modalSlice";
import { useAppDispatch } from "@/app/redux/hooks";

interface SavingsCardProps {
  totalSavings: number;
  currentSavings: number;
  currentPeriod: string;
  loading?: boolean;
  /** Decorative pass coin on the card. Management mobile Figma hides it. */
  showCoin?: "always" | "desktop" | "never";
}

export default function SavingsCard({
  totalSavings,
  currentSavings,
  currentPeriod,
  loading = false,
  showCoin = "always",
}: SavingsCardProps) {
  const dispatch = useAppDispatch();

  const openSavingsInfo = () => {
    dispatch(publishModalData({ modalData: <SavingsInfoModal /> }));
    dispatch(publishModalClasses({ classNames: "lg:w-[602px] relative overflow-y-auto lg:rounded-2xl" }));
    dispatch(publishModalVisibility({ isOpen: true }));
  };

  const coinVisibilityClass = showCoin === "never" ? "hidden" : showCoin === "desktop" ? "hidden lg:block" : "";

  const coin = (
    <img
      src="/assets/images/management-subscription/pass-coin.webp"
      alt=""
      className={`absolute -top-0.5 -right-2.5 lg:-top-[3px] lg:right-[-29px] size-16 lg:size-[97px] pointer-events-none select-none object-contain ${coinVisibilityClass}`}
    />
  );

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="relative rounded-xl lg:rounded-2xl overflow-hidden shadow-[0px_8px_10px_0px_rgba(0,0,0,0.1)] lg:shadow-[0px_10px_30px_0px_rgba(0,0,0,0.16)]">
        {totalSavings === 0 ? (
          <div className="relative bg-gray-4 lg:bg-white px-6 lg:px-10 py-4 lg:py-5 flex flex-col items-center gap-1">
            {coin}
            <p className="m-0 p-0 tracking-[0] text-[24px] lg:text-[32px] font-bold font-jost leading-7.5 lg:leading-10">
              🚀
            </p>
            <p className="m-0 p-0 tracking-[0] text-dark-1 text-[13px] lg:text-base font-medium font-jost leading-4.5 lg:leading-5.5 text-center">
              You just started your BOTB Pass membership!
            </p>
            <p className="m-0 p-0 tracking-[0] text-gray-1 text-[13px] lg:text-base font-medium font-jost leading-4.5 lg:leading-5.5 text-center">
              Your savings will appear here at the end of your first subscription cycle.
            </p>
          </div>
        ) : (
          <>
            <div className="relative flex flex-col items-center lg:items-start gap-1 lg:gap-0.5 px-4 lg:px-8 py-3 lg:pt-6 lg:pb-4 bg-gray-4 lg:bg-white">
              {coin}
              <div className="flex flex-col items-center lg:items-start gap-1 lg:gap-0.5 w-full">
                <p className="m-0 p-0 tracking-[0] text-dark-1 text-[11px] lg:text-sm font-medium font-jost leading-3.5 lg:leading-5 text-center lg:text-left">
                  Your total savings as a BOTB Pass Member
                </p>
                <div className="flex flex-col items-center lg:items-start">
                  <p
                    className={`m-0 p-0 tracking-[0] bg-gradient-botb bg-clip-text text-transparent text-2xl lg:text-[32px] font-bold font-jost leading-7.5 lg:leading-10 ${loading ? "opacity-30 blur-[5px]" : ""}`}>
                    {formatCurrency(totalSavings)}
                  </p>
                  <p className="m-0 p-0 tracking-[0] text-gray-1 text-[11px] lg:text-sm font-medium lg:font-normal font-jost leading-3.5 lg:leading-5 text-center lg:text-left">
                    Saved vs. paying full ticket price
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between lg:justify-center gap-2 lg:gap-4 px-2.5 lg:px-4 py-2.5 lg:py-2 bg-[linear-gradient(137deg,#1500ff_2%,#7366ff_101.5%)] lg:bg-[linear-gradient(148deg,#1500ff_2%,#7366ff_101.5%)]">
              <p
                className={`m-0 p-0 tracking-[0] text-white text-xs lg:text-base font-normal lg:font-medium font-jost leading-4 lg:leading-5.5 whitespace-nowrap ${loading ? "opacity-30 blur-[5px]" : ""}`}>
                {currentSavings === 0 ? (
                  <span className="animate-pulse-fade">Current savings building...</span>
                ) : (
                  <>
                    Current savings: <span className="font-medium lg:font-bold">+{formatCurrency(currentSavings)}</span>
                  </>
                )}
              </p>
              {currentPeriod && (
                <div
                  className={`bg-white/20 rounded-full px-2.5 py-0.5 shrink-0 ${loading ? "opacity-30 blur-[5px]" : ""}`}>
                  <p className="m-0 p-0 tracking-[0] text-white text-[10px] lg:text-sm font-medium font-jost leading-3 lg:leading-5 whitespace-nowrap">
                    {currentPeriod}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <p className="m-0 p-0 tracking-[0] text-xs leading-4 lg:text-[13px] lg:leading-4.5 font-normal font-jost text-left">
        <span className="text-gray-5">
          Current savings are added to your total savings at the end of each subscription cycle.{" "}
        </span>
        <button onClick={openSavingsInfo} className="text-gray-1 lg:text-secondary underline cursor-pointer">
          How does this work?
        </button>
      </p>
    </div>
  );
}
